import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ServicesStatus from '@/components/ServicesStatus/ServicesStatus';
import * as Const from '@/helpers/constants';
import { ROUTES, getAbsoluteRoute } from '@/helpers/constants';
import useAuth from '@/hooks/useAuth';
import useSave from '@/hooks/useSave';
import styles from './Menu.module.scss';
import { NavLink } from 'react-router';

type MenuProps = {
  setToggleMenu: (toggleMenu: (fromAdminTile: boolean) => void) => void;
};

type SubOption = {
  id: string;
  to: string;
  icon: string;
  text: string;
};

const idsEmails = ['links-emails', 'link-template-section', 'link-workflow-section'];

const Menu: React.FC<MenuProps> = ({ setToggleMenu }) => {
  const { t } = useTranslation();
  const authService = useAuth();
  const menuRef = useRef(null);
  const [openClass, setOpenClass] = useState('');
  const [openedFromAdminTile, setOpenedFromAdminTile] = useState(false);
  const [activeOptionId, setActiveOptionId] = useState('');

  const closeMenu = () => {
    setOpenClass('');
    setOpenedFromAdminTile(false);
  };

  const saveChanges = useSave(localStorage.getItem(Const.STORAGE.PREVIOUS_TAB));

  const toggleMenu = (fromAdminTile) => {
    if (window.location.pathname.toLowerCase().includes('/campaign/') && !openClass) {
      saveChanges();
    }

    const newClass = openClass === '' ? styles.Open : '';
    setOpenedFromAdminTile(fromAdminTile);

    setOpenClass(newClass);
  };

  useEffect(() => {
    setToggleMenu(() => toggleMenu);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleclick = useCallback((id, subOptions) => {
    const hasSubOptions = subOptions?.length > 0 ? true : false;
    setActiveOptionId((id === activeOptionId && id === 'link-emails' && !idsEmails.includes(id)) ? '' : id);

    if (!hasSubOptions) setOpenClass('');
  }, [activeOptionId, setActiveOptionId, setOpenClass]);

  const LiNavLink = useCallback(
    ({ id, to, icon, text, subOptions = [] }: { id: string; to: string; icon: string; text: string; subOptions?: SubOption[] }) => (
      <>
        <li className={styles.NavItem}>
          <NavLink
            id={id}
            to={to}
            className={({ isActive }) => `${styles.Link} ${isActive && activeOptionId === id ? styles.LinkSelected : ''}`}
            onClick={() => handleclick(id, subOptions)}
          >
            <i className={`di ${icon} ${styles.MenuIcon}`} />
            <span className={styles.ItemMenu}>{text}</span>
          </NavLink>
        </li>

        {id === activeOptionId && subOptions?.length > 0 && (
          <ul>
            {subOptions.map((subOption) => (
              <li key={subOption.id} className={styles.SubNavItem}>
                <NavLink
                  id={subOption.id}
                  to={subOption.to}
                  className={({ isActive }) => `${styles.Link} ${isActive ? styles.LinkSelected : ''}`}
                  onClick={() => setOpenClass('')}
                >
                  <i className={`di ${subOption.icon} ${styles.MenuIcon}`} />
                  <span className={styles.ItemMenu}>{subOption.text}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </>
    ),
    [setOpenClass, activeOptionId, handleclick],
  );

  return (
    <div ref={menuRef} className={`${styles.Menu} ${openClass}`} data-testid="menu">
      <i className={`di ${openClass ? 'icon-schliessen' : 'icon-burger-menue'} round-icon-big`} onClick={() => toggleMenu(false)} />
      <div className={`${styles.MenuLayout}`} onClick={closeMenu} />
      <div className={styles.MenuSection}>
        <ul>
          <LiNavLink
            id="dashboard"
            to={getAbsoluteRoute(ROUTES.DASHBOARD)}
            icon="icon-kalender-monat"
            text={t('menu.dashboard')}
          />
        </ul>
        {authService?.checkRoles([Const.CREATOR, Const.CREATOR_JV, Const.CREATOR_EXTERNAL, Const.ADMIN])
          && (
            <>
              <h6>{t('menu.campaigns')}</h6>
              <ul>
                <LiNavLink
                  id="campaign-overview"
                  to={getAbsoluteRoute(ROUTES.CR_OVERVIEW)}
                  icon="icon-datei-text"
                  text={t('menu.campaignRequestOverview')}
                />
              </ul>
            </>
          )}
        {authService?.checkRole(Const.ADMIN) && (
          <>
            <h6>{t('menu.administration')}</h6>
            <ul id="master-data-links" className={openedFromAdminTile ? styles.OpenedFromAdminTile : ''}>
              <LiNavLink
                id="link-plants"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/plants`)}
                icon="icon-werk-doppelt"
                text={t('menu.plants')}
              />
              <LiNavLink
                id="link-model-series"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/model-series`)}
                icon="icon-auto"
                text={t('hardware.modelSeries')}
              />
              <LiNavLink
                id="link-params-configuration"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/params-configuration`)}
                icon="icon-zahnrad-einstellungen-doppelt"
                text={t('menu.paramsConfiguration')}
              />
              <LiNavLink
                id="link-workflow-duration"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/workflow-duration`)}
                icon="icon-uhr-stoppuhr"
                text={t('menu.workflowDuration')}
              />
              <LiNavLink
                id="link-newsfeed"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/newsfeed`)}
                icon="icon-megafon-benachrichtigung"
                text={t('menu.newsfeed')}
              />
              <LiNavLink
                id="link-helps"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/help/summary`)}
                icon="icon-hilfe"
                text={t('menu.helpAutor')}
              />
              <LiNavLink
                id="link-fieldsMaintenance"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/fields/description`)}
                icon="icon-datei"
                text={t('menu.fields')}
              />
              <LiNavLink
                id="link-productGroups"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/product-groups`)}
                icon="icon-werk-daimler-ansprechpartner"
                text={t('menu.productGroup')}
              />
              <LiNavLink
                id="link-SUMSCriteria"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/sums-criteria`)}
                icon="icon-pfeil-aktualisieren"
                text={t('menu.sumsCriteria')}
              />
              <LiNavLink
                id="link-StandIns"
                to={getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/stand-ins`)}
                icon="icon-person-gruppe"
                text={t('menu.standIns')}
              />
              <LiNavLink
                id="link-emails"
                to="#"
                icon="icon-email"
                text={t('menu.emails')}
                subOptions={[
                  {
                    id: 'link-template-section',
                    to: getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/emails/templates`),
                    icon: 'icon-liste',
                    text: t('menu.templateSection'),
                  },
                  {
                    id: 'link-workflow-section',
                    to: getAbsoluteRoute(`${ROUTES.ADMINISTRATION}/emails/workflows`),
                    icon: 'icon-doppelpfeil-uebertragen',
                    text: t('menu.workflowSection'),
                  },
                ]}
              />
            </ul>
          </>
        )}
        <ServicesStatus />
      </div>
    </div>
  );
};

export default Menu;
